import {captureException} from '@sentry/browser';
import jwt_decode from 'jwt-decode';
import authApi from '../api/auth.api';
import {formatPostcode} from './string.helper';

import {
  ERRORS,
  HTTP_STATUS,
  MAX_RETRY,
  AX_MOBILE_REGEX,
  JJ_LOCAL_STORAGE,
  REGISTER_CUSTOMER_TYPES,
  GQL_ERROR_MESSAGE,
  REGISTER_WHERE_DID_YOU_HEAR_ABOUT_US_NA,
} from '../constants/constants';
import {
  getDataFromLocalStorage,
  persistDataToLocalStorage,
} from './localStorage.helper';
import {getApiConfig} from '../config/configProvider';
import {saveTokenValidTime} from './data.helper';
import {checkStaffAccount} from './accounts.helper';

let expiredAuthorization;
let newRefreshedToken;
let isIdle = false;
let pendingRefreshPromise = null;

export const setIdleMode = idle => {
  isIdle = idle;
};

export const getBearerHeader = (jjToken, storedTokens) => {
  let headers;
  if (jjToken || (storedTokens && storedTokens.access_token)) {
    const token = jjToken || storedTokens;
    headers = {
      Authorization: `Bearer ${
        typeof token === 'string' ? token : token.access_token
      }`,
    };
  }
  return headers;
};

const getTokenDataFromHeader = headers => {
  let token = null;
  if (headers && headers.Authorization) {
    const headerAuth = headers.Authorization.split('Bearer ');
    if (headerAuth && headerAuth[1]) {
      token = {
        access_token: headerAuth[1],
        grant_type: 'refresh_token',
      };
    }
  }
  return token;
};

// Performs the token refresh only — shared across concurrent 401s via pendingRefreshPromise.
// Returns {} on success or {error} on failure.
const doTokenRefresh = async (setAuthToken, res) => {
  const conf = getApiConfig();
  const refreshTokenData = await getDataFromLocalStorage(
    JJ_LOCAL_STORAGE.REFRESH_TOKEN
  );
  const tokenData = await getDataFromLocalStorage(JJ_LOCAL_STORAGE.TOKEN);
  if (!tokenData || !refreshTokenData) {
    return {error: (res && res.data) || res};
  }
  expiredAuthorization = getBearerHeader(tokenData);
  let jwtToken;
  try {
    jwtToken = jwt_decode(tokenData);
  } catch (e) {
    return {error: (res && res.data) || res};
  }
  const refreshData = {
    refresh_token: refreshTokenData,
    grant_type: 'refresh_token',
    scope: conf.azureScopes,
    account: jwtToken.c_account,
  };
  try {
    const token = await authApi.refresh(refreshData);
    if (token) {
      const {access_token, refresh_token} = token;
      const decodedToken = jwt_decode(access_token);
      setAuthToken({access_token, ...decodedToken});
      newRefreshedToken = getBearerHeader(access_token);
      persistDataToLocalStorage(JJ_LOCAL_STORAGE.TOKEN, access_token);
      persistDataToLocalStorage(JJ_LOCAL_STORAGE.REFRESH_TOKEN, refresh_token);
      if (decodedToken.expires_in) {
        saveTokenValidTime(decodedToken.expires_in);
      }
      return {};
    }
    return {error: (res && res.data) || res};
  } catch (err) {
    return {
      error: (err && err.response && err.response.data) || {
        tokenRefresh: 'failed to refresh token',
      },
    };
  }
};

// Returns the in-flight refresh promise if one is already running, otherwise starts a new one.
// This ensures only one token refresh call is made regardless of how many concurrent 401s arrive.
const getOrStartRefresh = (setAuthToken, res) => {
  if (!pendingRefreshPromise) {
    pendingRefreshPromise = doTokenRefresh(setAuthToken, res).finally(() => {
      pendingRefreshPromise = null;
    });
  }
  return pendingRefreshPromise;
};

export const axiosHelper = async (axios, setAuthToken) => {
  // Async interceptor: pauses any outgoing request that carries the expired token
  // until the in-flight refresh completes, then swaps in the new token.
  axios.interceptors.request.use(async req => {
    // Token refresh must never be cancelled for being "idle" — it's exactly
    // what keeps the session alive while the tab is backgrounded. Cancelling
    // it here made the refresh call fail, which the auth saga treats as a
    // real failure and force-logs-out the user (see REFRESH_AUTH_FAILED in
    // errors.reducer.js) — even though nothing was actually wrong with their
    // session.
    const isTokenRefreshRequest =
      req && req.url && req.url.includes('oauth/token');
    if (isIdle && !isTokenRefreshRequest) {
      const source = axios.CancelToken.source();
      req.cancelToken = source.token;
      source.cancel('idle');
      return req;
    }
    if (
      req &&
      req.headers &&
      req.headers.Authorization &&
      expiredAuthorization &&
      expiredAuthorization.Authorization === req.headers.Authorization
    ) {
      if (pendingRefreshPromise) {
        await pendingRefreshPromise;
      }
      if (newRefreshedToken) {
        req.headers.Authorization = newRefreshedToken.Authorization;
      }
    }
    return req;
  });
  axios.interceptors.response.use(
    async response => {
      if (
        response &&
        response.data &&
        response.data.errors &&
        response.data.errors.length === 1 &&
        response.data.errors[0] &&
        response.data.errors[0].message === GQL_ERROR_MESSAGE.UNAUTHORIZED
      ) {
        response.config._retryCount = (response.config._retryCount || 0) + 1;
        if (response.config._retryCount > MAX_RETRY) {
          return Promise.reject({
            code: ERRORS.NEED_AUTH,
            originalError: response.data,
          });
        }
        const refreshResult = await getOrStartRefresh(setAuthToken, response);
        if (refreshResult.error) {
          return Promise.reject({
            code: ERRORS.NEED_AUTH,
            originalError: refreshResult.error,
          });
        }
        response.config.headers['Authorization'] =
          newRefreshedToken.Authorization;
        return axios(response.config);
      }
      return response;
    },
    async error => {
      if (axios.isCancel(error)) {
        return Promise.resolve(null);
      }
      if (error && error.response) {
        const {status, data} = error.response;

        // Handle no internet connection
        if (status === HTTP_STATUS.NETWORK_NOT_AVAILABLE) {
          return Promise.reject({error: 'No internet connection'});
        }

        if (
          status === HTTP_STATUS.UNAUTHORIZED &&
          error.config &&
          !error.config.url.includes('oauth/token')
        ) {
          // Staff accounts should not silently refresh — surface the 401 immediately
          const tokenData = await getDataFromLocalStorage(
            JJ_LOCAL_STORAGE.TOKEN
          );
          if (tokenData) {
            try {
              const jwtToken = jwt_decode(tokenData);
              if (checkStaffAccount(jwtToken.authorities)) {
                return Promise.reject({
                  code: ERRORS.NEED_AUTH,
                  originalError: data,
                });
              }
            } catch (e) {
              // ignore decode errors, fall through to refresh
            }
          }

          error.config._retryCount = (error.config._retryCount || 0) + 1;
          if (error.config._retryCount > MAX_RETRY) {
            return Promise.reject({
              code: ERRORS.NEED_AUTH,
              originalError: data,
            });
          }

          const refreshResult = await getOrStartRefresh(setAuthToken, error);
          if (refreshResult.error) {
            return Promise.reject({
              code: ERRORS.NEED_AUTH,
              originalError: refreshResult.error,
            });
          }
          error.config.headers['Authorization'] =
            newRefreshedToken.Authorization;
          return axios(error.config);
        }
        if (
          data &&
          (data.code === ERRORS.HTTP_SERVER_ERROR ||
            data.code === ERRORS.ILLEGAL_ARGUMENT)
        ) {
          return Promise.reject(data);
        }
        if (status === HTTP_STATUS.SERVER_ERROR) {
          return Promise.reject(data);
        }
      }
      return Promise.reject(error);
    }
  );
};

const getPhoneNumber = num => {
  const phone = num.replace(/ /g, '');
  return phone[0] === '0' ? phone.substr(1) : phone;
};

const checkPhoneType = num => {
  if (AX_MOBILE_REGEX.test(num)) {
    return 2;
  }
  return 1;
};

export const convertToOldRegisterApi = data => {
  let phonePrimary, phoneAlternative, phonePrimaryType, phoneAlternativeType;
  try {
    const {phone1, phone2} = data;
    phonePrimary = getPhoneNumber(phone1);
    phonePrimaryType = checkPhoneType(phonePrimary);
    if (phone2) {
      phoneAlternative = getPhoneNumber(phone2);
      phoneAlternativeType = checkPhoneType(phoneAlternative);
    }
  } catch (e) {
    captureException(e);
  }
  return {
    type: 'json',
    Version: 'v1',
    AccountType: data.isBusiness ? 2 : 1,
    CustomerName: `${data.firstname} ${data.surname}`,
    DateofBirthString: '10/10/2001',
    Country: 'GBR',
    County: data.county,
    Street: data.address,
    Postcode: formatPostcode(data.postcode),
    City: data.place,
    InvalidAddress: false,
    CustomerContactPrimary: phonePrimary,
    PhonePrimaryType: phonePrimaryType,
    CustomerContactAlternative: phoneAlternative,
    PhoneAlternativeType: phoneAlternativeType,
    CustomerEmail: data.email,
    CustomerPassword: data.password,
    latitude: data.latitude,
    longitude: data.longitude,
    ...(data.isBusiness && {
      BusinessName: data.businessName,
      TradingName: data.businessName,
    }),
    CustomerType: data.businessType || REGISTER_CUSTOMER_TYPES.CNM,
    marketing: data.marketing,
    ...(data.whereDidYouHearAboutUs !==
      REGISTER_WHERE_DID_YOU_HEAR_ABOUT_US_NA &&
      data.whereDidYouHearAboutUs && {
        WhereDidYouHearAboutUs: data.whereDidYouHearAboutUs,
      }),
  };
};

export const getTimestamp = isAppending => {
  const timestamp = Math.floor(Date.now() / 1000);
  return `${isAppending ? '&' : '?'}timestamp=${timestamp}`;
};
